import React, { useEffect, useMemo, useState } from 'react';
import DecisionEngineBlock from '../../decision-engine/DecisionEngineBlock';
import DrawerFooterActions from './DrawerFooterActions';
import { fetchSegmentCatalog, fetchSegmentBuilderPreview, varsayilanBuilderPayload } from '../../../../lib/admin/segmentContractClient';

const YASAM_DONGUSU_SIRASI = ['taslak', 'kaydedildi', 'incelemede', 'yayinlandi', 'arsivlendi'];

function etiketlestirYasamDongusu(durum) {
  const d = String(durum || '').toLowerCase();
  if (d === 'taslak') return 'Taslak';
  if (d === 'kaydedildi') return 'Kaydedildi';
  if (d === 'incelemede') return 'İncelemede';
  if (d === 'yayinlandi') return 'Yayımlandı';
  if (d === 'arsivlendi') return 'Arşivlendi';
  return 'Taslak';
}

function etiketlestirSecenek(value) {
  const v = String(value || '');
  return v
    .replaceAll('-', ' ')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function kosulOlustur(grupAnahtar, alanAnahtar, islemAnahtar, deger) {
  return { grup: grupAnahtar, alan: alanAnahtar, islem: islemAnahtar, deger };
}

function varsayilanKosullar(payload) {
  const list = Array.isArray(payload?.kosullar) ? payload.kosullar : [];
  return list.slice(0, 2).map((k) => kosulOlustur(k.grup, k.alan, k.islem, k.deger));
}

function uygunIslemListesi(islemListesi, kip) {
  return (Array.isArray(islemListesi) ? islemListesi : []).filter((item) => {
    const kipler = Array.isArray(item?.kipler) ? item.kipler : [];
    return kipler.includes(kip);
  });
}

function alanGruplariForKip(gruplar, kip) {
  const rows = Array.isArray(gruplar) ? gruplar : [];
  if (kip === 'ileri_duzey') return rows;
  return rows.filter((g) => !g?.yalnizcaIleriDuzey);
}

function alanBul(katalog, grupAnahtar, alanAnahtar) {
  const grup = (katalog?.alanGruplari || []).find((g) => g.anahtar === grupAnahtar);
  if (!grup) return null;
  return (grup.alanlar || []).find((a) => a.anahtar === alanAnahtar) || null;
}

export default function SegmentBuilderDrawer({ entity, onClose }) {
  const source = entity?.payload?.source || 'dashboard';
  const canAdvancedMode = Boolean(entity?.payload?.permissions?.canAdvancedMode);
  const canPublish = Boolean(entity?.payload?.permissions?.canPublish);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [katalog, setKatalog] = useState(null);
  const [onizleme, setOnizleme] = useState(null);
  const [kip, setKip] = useState('yonlendirmeli');
  const [yasamDongusu, setYasamDongusu] = useState('taslak');
  const [kosullar, setKosullar] = useState([]);

  useEffect(() => {
    let aktif = true;
    (async () => {
      try {
        setYukleniyor(true);
        const payload = varsayilanBuilderPayload();
        const katalogJson = await fetchSegmentCatalog();
        const kipDegeri = String(katalogJson?.varsayilanKip || payload.kip || 'yonlendirmeli');
        const ilkKosullar = varsayilanKosullar(payload);
        const onizlemeJson = await fetchSegmentBuilderPreview({
          kip: kipDegeri,
          pencere: payload.pencere || 'default',
          kosullar: ilkKosullar
        });
        if (!aktif) return;
        setKatalog(katalogJson);
        setKip(kipDegeri);
        setKosullar(ilkKosullar);
        setOnizleme(onizlemeJson);
      } catch (error) {
        console.error('SegmentBuilderDrawer yükleme hatası', error);
      } finally {
        if (aktif) setYukleniyor(false);
      }
    })();
    return () => {
      aktif = false;
    };
  }, []);

  useEffect(() => {
    if (!katalog) return;
    let aktif = true;
    const timer = setTimeout(async () => {
      try {
        const onizlemeJson = await fetchSegmentBuilderPreview({
          kip,
          pencere: 'default',
          kosullar: kosullar.slice(0, 2)
        });
        if (!aktif) return;
        setOnizleme(onizlemeJson);
      } catch (error) {
        console.error('SegmentBuilderDrawer önizleme hatası', error);
      }
    }, 240);
    return () => {
      aktif = false;
      clearTimeout(timer);
    };
  }, [kip, kosullar, katalog]);

  const kombinasyonSiniri = Number(katalog?.kombinasyonSiniri || 2);
  const kipListesi = useMemo(() => {
    const base = Array.isArray(katalog?.olusturucuKipleri) ? katalog.olusturucuKipleri : ['yonlendirmeli', 'ileri_duzey'];
    return canAdvancedMode ? base : base.filter((k) => k !== 'ileri_duzey');
  }, [katalog?.olusturucuKipleri, canAdvancedMode]);
  const grupListesi = useMemo(() => alanGruplariForKip(katalog?.alanGruplari, kip), [katalog?.alanGruplari, kip]);
  const islemListesi = useMemo(() => uygunIslemListesi(katalog?.islemListesi, kip), [katalog?.islemListesi, kip]);

  useEffect(() => {
    if (!canAdvancedMode && kip === 'ileri_duzey') {
      setKip('yonlendirmeli');
    }
  }, [canAdvancedMode, kip]);

  const guven = onizleme?.guven || {};
  const kararDili = onizleme?.kararDili || {};
  const yayinRedNedenleri = Array.isArray(onizleme?.yayinRedNedenleri) ? onizleme.yayinRedNedenleri : [];

  const yayinUygunMu = useMemo(() => {
    const redVar = yayinRedNedenleri.length > 0;
    return !redVar
      && onizleme?.uygunluk !== 'engelli_dusuk_orneklem'
      && onizleme?.uygunluk !== 'engelli_hassas_kapsam'
      && canPublish;
  }, [onizleme?.uygunluk, yayinRedNedenleri, canPublish]);

  const eylemListesi = useMemo(() => {
    if (yayinRedNedenleri.length > 0) {
      return [
        `Yayın öncesi düzeltme: ${yayinRedNedenleri.join(', ')}`,
        'Örneklem yeterli değilse yalnız gözlem düzeyinde tut.',
        'Hassas kapsam varsa gelir etiketini kapalı bırak.'
      ];
    }
    return [
      'Koşul setini doğrula ve uygunluk düzeyine göre yayınla.',
      'Güven bandı düşükse eylem dilini gözlem seviyesinde tut.',
      'Açıklanabilirlik alanında ana neden ve destek metrikleri zorunlu doldur.'
    ];
  }, [yayinRedNedenleri]);

  const kosulEkle = () => {
    if (kosullar.length >= kombinasyonSiniri) return;
    const ilkGrup = grupListesi[0];
    const ilkAlan = ilkGrup?.alanlar?.[0];
    const ilkIslem = islemListesi[0];
    const ilkDeger = Array.isArray(ilkAlan?.secenekler) && ilkAlan.secenekler.length > 0 ? ilkAlan.secenekler[0] : '';
    setKosullar((prev) => [...prev, kosulOlustur(ilkGrup?.anahtar || '', ilkAlan?.anahtar || '', ilkIslem?.anahtar || 'esittir', ilkDeger)]);
  };

  const kosulSil = (index) => {
    setKosullar((prev) => prev.filter((_, i) => i !== index));
  };

  const kosulGuncelle = (index, alan, deger) => {
    setKosullar((prev) => prev.map((item, i) => {
      if (i !== index) return item;
      const next = { ...item, [alan]: deger };
      if (alan === 'grup') {
        const grup = grupListesi.find((g) => g.anahtar === deger);
        const ilkAlan = grup?.alanlar?.[0];
        next.alan = ilkAlan?.anahtar || '';
        const varsDeger = Array.isArray(ilkAlan?.secenekler) && ilkAlan.secenekler.length > 0 ? ilkAlan.secenekler[0] : '';
        next.deger = varsDeger;
      }
      if (alan === 'alan') {
        const hedefAlan = alanBul(katalog, next.grup, deger);
        const varsDeger = Array.isArray(hedefAlan?.secenekler) && hedefAlan.secenekler.length > 0 ? hedefAlan.secenekler[0] : '';
        if (varsDeger !== '') next.deger = varsDeger;
      }
      return next;
    }));
  };

  const durumDegistir = (hedefDurum) => {
    const hedefIndex = YASAM_DONGUSU_SIRASI.indexOf(hedefDurum);
    const mevcutIndex = YASAM_DONGUSU_SIRASI.indexOf(yasamDongusu);
    if (hedefIndex < 0 || mevcutIndex < 0) return;
    if (hedefDurum === 'yayinlandi' && !yayinUygunMu) return;
    if (hedefIndex === mevcutIndex + 1 || hedefDurum === 'arsivlendi' || hedefDurum === 'taslak') {
      setYasamDongusu(hedefDurum);
    }
  };

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900">
        <p className="text-xs text-slate-500 dark:text-slate-400">Segment Oluşturucu Giriş Alanı</p>
        <h4 className="mt-1 text-lg font-black text-slate-900 dark:text-white">Yönlendirmeli oluşturucu</h4>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Kaynak: {source} | Önce anlam bütünlüğü, sonra yayın.
        </p>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Oluşturucu Kipi</p>
          <select
            value={kip}
            onChange={(e) => setKip(String(e.target.value))}
            className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold dark:border-slate-700 dark:bg-slate-900"
          >
            {kipListesi.map((k) => (
              <option key={k} value={k}>
                {k === 'yonlendirmeli' ? 'Yönlendirmeli Kip' : 'İleri Düzey İşaret Kipi'}
              </option>
            ))}
          </select>
        </div>
        {!canAdvancedMode ? (
          <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
            İleri düzey kip yalnız yönetim yetkisi olan kullanıcılara açıktır.
          </p>
        ) : null}
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Maksimum koşul: {kombinasyonSiniri} | Şu an: {kosullar.length}
        </p>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Koşullar</p>
          <button
            type="button"
            onClick={kosulEkle}
            disabled={kosullar.length >= kombinasyonSiniri}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold disabled:opacity-50 dark:border-slate-700"
          >
            Koşul Ekle
          </button>
        </div>

        <div className="mt-2 space-y-2">
          {kosullar.map((kosul, index) => {
            const grup = grupListesi.find((g) => g.anahtar === kosul.grup) || grupListesi[0];
            const alanlar = Array.isArray(grup?.alanlar) ? grup.alanlar : [];
            const seciliAlan = alanlar.find((a) => a.anahtar === kosul.alan) || alanlar[0];
            const secenekler = Array.isArray(seciliAlan?.secenekler) ? seciliAlan.secenekler : [];
            const sayisalAlan = String(seciliAlan?.tur || '') === 'sayi';

            return (
              <div key={`kosul-${index}`} className="rounded-md border border-slate-200 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-800/50">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
                  <select
                    value={kosul.grup}
                    onChange={(e) => kosulGuncelle(index, 'grup', e.target.value)}
                    className="h-8 rounded border border-slate-200 bg-white px-2 text-xs dark:border-slate-700 dark:bg-slate-900"
                  >
                    {grupListesi.map((g) => (
                      <option key={g.anahtar} value={g.anahtar}>{g.etiket}</option>
                    ))}
                  </select>

                  <select
                    value={kosul.alan}
                    onChange={(e) => kosulGuncelle(index, 'alan', e.target.value)}
                    className="h-8 rounded border border-slate-200 bg-white px-2 text-xs dark:border-slate-700 dark:bg-slate-900"
                  >
                    {alanlar.map((a) => (
                      <option key={a.anahtar} value={a.anahtar}>{a.etiket}</option>
                    ))}
                  </select>

                  <select
                    value={kosul.islem}
                    onChange={(e) => kosulGuncelle(index, 'islem', e.target.value)}
                    className="h-8 rounded border border-slate-200 bg-white px-2 text-xs dark:border-slate-700 dark:bg-slate-900"
                  >
                    {islemListesi.map((i) => (
                      <option key={i.anahtar} value={i.anahtar}>{i.etiket}</option>
                    ))}
                  </select>

                  {secenekler.length > 0 ? (
                    <select
                      value={kosul.deger}
                      onChange={(e) => kosulGuncelle(index, 'deger', e.target.value)}
                      className="h-8 rounded border border-slate-200 bg-white px-2 text-xs dark:border-slate-700 dark:bg-slate-900"
                    >
                      {secenekler.map((s) => (
                        <option key={s} value={s}>{etiketlestirSecenek(s)}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={sayisalAlan ? 'number' : 'text'}
                      value={kosul.deger}
                      onChange={(e) => kosulGuncelle(index, 'deger', sayisalAlan ? Number(e.target.value || 0) : e.target.value)}
                      className="h-8 rounded border border-slate-200 bg-white px-2 text-xs dark:border-slate-700 dark:bg-slate-900"
                      placeholder="Değer"
                    />
                  )}
                </div>
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={() => kosulSil(index)}
                    className="rounded-md border border-rose-200 px-2 py-1 text-xs font-semibold text-rose-700 dark:border-rose-700 dark:text-rose-300"
                  >
                    Koşulu Sil
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Önizleme Özeti</p>
        {yukleniyor ? (
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Önizleme hazırlanıyor...</p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm text-slate-700 dark:text-slate-200">
            <li>Koşul sayısı: {Number(onizleme?.ozet?.kosulSayisi || 0)}</li>
            <li>Uygunluk: {String(onizleme?.uygunluk || 'yalniz_icgoru').replaceAll('_', ' ')}</li>
            <li>Güven bandı: {String(guven?.guvenBandi || 'dusuk')}</li>
            <li>Örneklem: {Number(guven?.orneklemBoyutu || 0).toLocaleString('tr-TR')}</li>
            <li>Örneklem yeterli mi: {guven?.orneklemYeterliMi ? 'Evet' : 'Hayır'}</li>
          </ul>
        )}
        {yayinRedNedenleri.length > 0 ? (
          <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            Yayın reddi nedenleri: {yayinRedNedenleri.join(', ')}
          </div>
        ) : null}
        {!canPublish ? (
          <div className="mt-2 rounded-md border border-slate-300 bg-slate-50 p-2 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
            Yayımlama adımı yalnız onay yetkisi bulunan kullanıcılara açıktır.
          </div>
        ) : null}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Yaşam Döngüsü</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {YASAM_DONGUSU_SIRASI.map((durum) => (
            <button
              key={durum}
              type="button"
              onClick={() => durumDegistir(durum)}
              disabled={durum === 'yayinlandi' && !yayinUygunMu}
              className={`rounded-md border px-2 py-1 text-xs font-semibold ${
                yasamDongusu === durum
                  ? 'border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900'
                  : 'border-slate-200 text-slate-700 dark:border-slate-700 dark:text-slate-200'
              } disabled:opacity-50`}
            >
              {etiketlestirYasamDongusu(durum)}
            </button>
          ))}
        </div>
      </section>

      <DecisionEngineBlock
        narrative={
          onizleme?.aciklama?.buSegmentNeyiAnlatir
          || 'Segment oluşturucu, koşul setini karar uygunluğu ile birlikte değerlendirir.'
        }
        actions={eylemListesi}
        priority={String(kararDili?.kararDuzeyi || 'gozlem').toUpperCase()}
        confidence={String(guven?.guvenBandi || 'dusuk').toUpperCase()}
        sampleSize={Number(guven?.orneklemBoyutu || 0)}
        expectedOutcome={onizleme?.aciklama?.neYapabiliriz || 'Uygun koşullarda eylem adayı üretilir, düşük örneklemde gözlemde kalır.'}
      />

      <DrawerFooterActions onClose={onClose} onExport={null} />
    </div>
  );
}
